// The floating "ask why" chat for the agent debug dashboard.
//
// Posts a question scoped to the turn currently on screen; the worker answers with
// `gpt-5.4-mini` over that turn's whole trace and publishes a `debug.chat.reply`
// on the employee's own private channel, which this subscribes to. The question
// only ENQUEUES (doctrine 1.1: no LLM work on the web cycle) and the answer
// arrives over Pusher (doctrine 1.2: no polling for it).
//
// The channel name is derived from the identity the SHELL already loaded
// (`GET /api/admin/me`), not from the build-time stub email: in cloud the SPA
// cannot read the HttpOnly authority cookie, and guessing would subscribe to a
// channel the backend refuses to sign and then silently receive nothing.

import { useEffect, useRef, useState } from "react";
import { Button, Composer, IconButton, usePusherChannel } from "@recruiter/shared";
import type { DebugChatReply } from "@recruiter/shared/types";
import { useQuery } from "@tanstack/react-query";

import { ApiError, postDebugChat } from "../lib/api";
import { debugChannelFor, employeePusherConfig } from "../lib/pusher";
import { meQuery } from "../lib/queries";
import { CloseIcon } from "./icons";

interface ChatLine {
  key: number;
  role: "user" | "assistant";
  content: string;
  ok: boolean;
}

export function DebugChat({ targetTurnId }: { targetTurnId: string | null }) {
  const me = useQuery(meQuery);
  const email = me.data?.email ?? null;

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [lines, setLines] = useState<ChatLine[]>([]);
  const [pending, setPending] = useState(false);
  const keyRef = useRef(0);
  // Question ids WE asked, and the ones already answered. The channel is the
  // employee's, not this tab's, so a second open tab's replies arrive here too and
  // would otherwise be appended to a conversation that never asked them.
  const askedRef = useRef<Set<string>>(new Set());
  const seenRepliesRef = useRef<Set<string>>(new Set());
  const processedRef = useRef(0);

  const events = usePusherChannel<DebugChatReply>(
    email ? debugChannelFor(email) : null,
    ["debug.chat.reply"],
    employeePusherConfig,
  );

  // Drain newly-arrived replies that answer one of our questions.
  useEffect(() => {
    for (let i = processedRef.current; i < events.length; i++) {
      const ev = events[i];
      if (!ev || !askedRef.current.has(ev.question_id)) continue;
      if (seenRepliesRef.current.has(ev.question_id)) continue;
      seenRepliesRef.current.add(ev.question_id);
      keyRef.current += 1;
      setLines((prev) => [
        ...prev,
        { key: keyRef.current, role: "assistant", content: ev.answer, ok: ev.ok },
      ]);
      setPending(false);
    }
    processedRef.current = events.length;
  }, [events]);

  async function send() {
    const question = input.trim();
    if (!question || !targetTurnId || pending) return;
    keyRef.current += 1;
    const history = lines.map((l) => ({ role: l.role, content: l.content }));
    setLines((prev) => [
      ...prev,
      { key: keyRef.current, role: "user", content: question, ok: true },
    ]);
    setInput("");
    setPending(true);
    try {
      const ack = await postDebugChat({ target_turn_id: targetTurnId, question, history });
      askedRef.current.add(ack.question_id);
    } catch (error) {
      // Surfaced in the log as a failed line rather than swallowed (doctrine 1.5).
      const detail = error instanceof ApiError ? error.detail : "Something went wrong.";
      keyRef.current += 1;
      setLines((prev) => [
        ...prev,
        { key: keyRef.current, role: "assistant", content: `Failed to ask: ${detail}`, ok: false },
      ]);
      setPending(false);
    }
  }

  if (!open) {
    return (
      <Button
        className="dbg-fab"
        size="sm"
        tone="accent"
        variant="solid"
        onClick={() => setOpen(true)}
      >
        Ask why
      </Button>
    );
  }

  return (
    <div className="dbg-chat">
      <header className="dbg-chat__head">
        <strong>Ask the trace</strong>
        <IconButton
          className="dbg-chat__close"
          size="sm"
          tone="neutral"
          variant="ghost"
          onClick={() => setOpen(false)}
          aria-label="Close"
        >
          <CloseIcon size={18} />
        </IconButton>
      </header>
      <div className="dbg-chat__scope">
        {targetTurnId ? (
          <>
            Scoped to turn <code>{targetTurnId}</code>
          </>
        ) : (
          <span className="dbg-muted">Pick a turn on the left to ask about it.</span>
        )}
      </div>
      <div className="dbg-chat__log">
        {lines.length === 0 ? (
          <p className="dbg-muted">
            Ask why an agent produced what it did, e.g. &ldquo;why did MASON drop the pricing
            section?&rdquo;
          </p>
        ) : (
          lines.map((line) => (
            <div
              key={line.key}
              className={`dbg-chat__line dbg-chat__line--${line.role}${
                line.ok ? "" : " dbg-chat__line--err"
              }`}
            >
              <pre className="dbg-pre">{line.content}</pre>
            </div>
          ))
        )}
        {pending ? (
          <div className="dbg-chat__line dbg-chat__line--assistant dbg-muted">thinking…</div>
        ) : null}
      </div>
      <div className="dbg-chat__compose">
        <Composer
          className="dbg-chat__input"
          size="sm"
          minRows={1}
          placeholder={targetTurnId ? "Ask why…" : "Pick a turn first"}
          value={input}
          disabled={!targetTurnId || pending}
          onChange={(e) => setInput(e.target.value)}
          submitOnEnter
          onSubmit={() => void send()}
          aria-label="Ask the trace"
        />
        <Button
          size="sm"
          tone="accent"
          variant="solid"
          onClick={() => void send()}
          disabled={!targetTurnId || pending || !input.trim()}
        >
          Send
        </Button>
      </div>
    </div>
  );
}
